import {
    closestCenter,
    type CollisionDetection,
    DndContext,
    type DragEndEvent,
    DragOverlay,
    type DragStartEvent,
    MeasuringStrategy,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    restrictToParentElement,
    restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { MoneyMapAccountWithAccountDetails } from '@fruitful/types';
import { MoneyMapRuleType, PaycheckRoutingRuleType } from '@fruitful/types';
import { useAtom, useSetAtom } from 'jotai';
import React, {
    type CSSProperties,
    type ReactNode,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
    ArrowStraight,
    Button,
    ButtonVariant,
    GripVerticalIcon,
    Icon,
    Notice,
    View,
    XStack,
} from 'smoothie';

import {
    editableMoneyMapAtom,
    reorderRuleSetsById,
    selectedMoneyMapRuleAtom,
} from '../state/editableMoneyMapAtom';
import {
    computeEditableRuleSets,
    type EditableRuleSet,
    getFirstIncomeSplitStopRuleSetId,
} from '../utils/editableRuleSetUtils';
import {
    applyReorderedGoals,
    type FlatRow,
    flattenRuleSets,
    getSetHeaderRows,
    getSetIdsInOrder,
} from '../utils/flatRuleRows';
import { MONTHLY_EXPENSES_SET_ID } from '../utils/ruleSetIds';
import { MoneyMapAddGoalSheet } from './MoneyMapAddGoalSheet';
import { MoneyMapAddRuleSetSheet } from './MoneyMapAddRuleSetSheet';
import { MoneyMapRoutingRuleCard } from './MoneyMapRoutingRuleCard';
import { MoneyMapRuleEditSheet } from './MoneyMapRuleEditSheet';
import { MoneyMapRuleSetEditSheet } from './MoneyMapRuleSetEditSheet';
import {
    CondensedSetFooter,
    getSetTotals,
    GoalDropSlot,
    GoalPlaceholderRow,
    MonthlyExpensesCard,
    SetFooterContent,
    SetHeaderContent,
} from './MoneyMapRuleSetParts';

interface MoneyMapRoutingRulesListProps {
    accounts: MoneyMapAccountWithAccountDetails[];
    canAddGoals: boolean;
    currentUserId: string;
    // Only used on native, where the list is the page's scroller. On web the
    // page already has a scroll view around this list.
    header?: ReactNode;
    footer?: ReactNode;
    isReorderable?: boolean;
}

// Space kept between a dropped set and the edge of the scroll area when
// scrolling it into view.
const SCROLL_INTO_VIEW_MARGIN = 16;

// While a whole set is dragged, every set collapses to its header so sets of
// any size are easy to move past each other. Turn off to drag a set's header
// with its goals left in place (the drop result is the same).
const COLLAPSE_SETS_WHILE_DRAGGING = true;

interface DragBindings {
    setActivatorNodeRef: (element: HTMLElement | null) => void;
    listeners: Record<string, unknown> | undefined;
    attributes: Record<string, unknown>;
}

interface RowContext {
    canAddGoals: boolean;
    firstStopSetId: string | null;
    isCollapsed: boolean;
    // While a goal is dragged, every set condenses (see CondensedSetFooter).
    isCondensingGoals: boolean;
    draggedGoalKey: string | null;
    // The number to show on each set's header while a set is being dragged: its
    // place in the order the sets would have if it were dropped now.
    liveOrdinals: ReadonlyMap<string, number> | null;
    onEditRule: (ruleId: string) => void;
    onEditRuleSet: (ruleSet: EditableRuleSet) => void;
    onAddGoal: (setId: string) => void;
}

const DragHandle = ({
    setActivatorNodeRef,
    listeners,
    attributes,
}: DragBindings) => {
    // dnd-kit's `role="button"` isn't a valid role prop on these views.
    const { role: _omitRole, ...safeAttributes } = attributes;

    return (
        <View
            ref={setActivatorNodeRef}
            {...listeners}
            {...safeAttributes}
            alignItems="center"
            justifyContent="center"
            minHeight={32}
            minWidth={32}
            cursor="grab"
        >
            <Icon icon={GripVerticalIcon} size={16} color="$gray1200" />
        </View>
    );
};

// A goal set is a header row, one row per goal and a footer row, which
// together look like one gray card. `drag` is absent when not reorderable.
const RowBody = ({
    row,
    ctx,
    drag,
    isDragging,
}: {
    row: FlatRow;
    ctx: RowContext;
    drag?: DragBindings;
    isDragging: boolean;
}) => {
    switch (row.kind) {
        case 'set-header':
            return (
                // Collapsed (while sets are being sorted) a header is a whole card.
                <div style={{ paddingBottom: ctx.isCollapsed ? 8 : 0 }}>
                    <XStack
                        backgroundColor="$gray200"
                        paddingHorizontal={16}
                        paddingTop={16}
                        paddingBottom={ctx.isCollapsed ? 16 : 13}
                        borderTopLeftRadius={24}
                        borderTopRightRadius={24}
                        borderBottomLeftRadius={ctx.isCollapsed ? 24 : 0}
                        borderBottomRightRadius={ctx.isCollapsed ? 24 : 0}
                        gap={9}
                        alignItems="center"
                    >
                        <SetHeaderContent
                            ruleSet={row.ruleSet}
                            ordinal={
                                ctx.liveOrdinals?.get(row.key) ?? row.ordinal
                            }
                            onEdit={() => {
                                ctx.onEditRuleSet(row.ruleSet);
                            }}
                            handle={drag != null && <DragHandle {...drag} />}
                        />
                    </XStack>
                </div>
            );
        case 'goal': {
            if (ctx.isCondensingGoals && !isDragging) {
                return <GoalPlaceholderRow />;
            }
            const card = (
                <MoneyMapRoutingRuleCard
                    {...row.rule}
                    percentageWarning={
                        getSetTotals(row.ruleSet).percentageNot100
                    }
                    editable
                    onPressEdit={() => {
                        ctx.onEditRule(row.rule.ruleId);
                    }}
                    setActivatorNodeRef={drag?.setActivatorNodeRef}
                    dragListeners={drag?.listeners}
                    dragAttributes={
                        drag?.attributes as React.ComponentProps<
                            typeof MoneyMapRoutingRuleCard
                        >['dragAttributes']
                    }
                />
            );
            // The goal being dragged leaves a slot behind, the size of a
            // placeholder (the lifted card is drawn separately, in the drag
            // overlay). The card stays in it, invisible and out of the layout,
            // to keep the drag handle.
            if (ctx.isCondensingGoals) {
                return <GoalDropSlot>{card}</GoalDropSlot>;
            }
            return (
                <View
                    // In the overlay, the row drops its background so only the
                    // goal card itself shows, not a strip of the set's card.
                    backgroundColor={isDragging ? 'transparent' : '$gray200'}
                    paddingHorizontal={16}
                    paddingBottom={8}
                >
                    {card}
                </View>
            );
        }
        case 'set-footer':
            if (ctx.isCondensingGoals) {
                return (
                    <CondensedSetFooter
                        isEmpty={row.ruleSet.rules.every(
                            (rule) =>
                                `goal:${rule.ruleId}` === ctx.draggedGoalKey,
                        )}
                    />
                );
            }
            return (
                <SetFooterContent
                    ruleSet={row.ruleSet}
                    isLast={row.isLast}
                    showStopWarning={row.setId === ctx.firstStopSetId}
                    canAddGoals={ctx.canAddGoals}
                    onAddGoal={() => {
                        ctx.onAddGoal(row.setId);
                    }}
                />
            );
    }
};

const SortableRow = ({
    row,
    ctx,
    registerNode,
}: {
    row: FlatRow;
    ctx: RowContext;
    registerNode: (key: string, node: HTMLElement | null) => void;
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: row.key });

    const style: CSSProperties = {
        transform: CSS.Translate.toString(transform),
        // The slot a dragged goal leaves behind jumps to where the goal would
        // land; sliding there lags behind the card and reads as bouncing.
        transition:
            isDragging && ctx.isCondensingGoals ? undefined : transition,
        position: 'relative',
        zIndex: isDragging ? 10 : undefined,
        // Makes the item being dragged look lifted off the page. A dragged goal
        // is drawn in the drag overlay instead, and leaves a slot here.
        filter:
            isDragging && !ctx.isCondensingGoals
                ? 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.25))'
                : undefined,
    };

    // Only headers and goals can be picked up, and only by their handle.
    const canDrag = row.kind !== 'set-footer';

    return (
        <div
            ref={(node) => {
                setNodeRef(node);
                registerNode(row.key, node);
            }}
            style={style}
        >
            <RowBody
                row={row}
                ctx={ctx}
                isDragging={isDragging}
                drag={
                    canDrag
                        ? {
                              setActivatorNodeRef,
                              listeners,
                              attributes: attributes as unknown as Record<
                                  string,
                                  unknown
                              >,
                          }
                        : undefined
                }
            />
        </div>
    );
};

// The nearest ancestor that actually scrolls, or null when the page itself does.
// Which row a dragged item is over. For a goal this is the row under the
// leading edge of the card (its bottom when moving down, its top when moving
// up), so the slot opens up in the next row in the direction of travel. Matching
// centers instead, a card much taller than the placeholders around it ends up
// with the slot a half card behind it. Sets are all the same size, so the
// default does for them.
//
// The direction is the direction the card last moved, not its direction from
// where the drag began, so reversing takes effect straight away.
//
// A goal moving up can't be over the first set's header: that would open its
// landing spot above the header, outside any set. The row after the header
// stands in for it. (Moving down onto the header is fine: the spot opens after
// it, inside the set.)
const createCollisionDetection = (
    getFirstHeader: () => { headerKey: string; nextKey: string } | null,
): {
    detect: CollisionDetection;
    reset: () => void;
} => {
    let lastTop: number | null = null;
    let movingDown = true;

    const detect: CollisionDetection = (args) => {
        if (!String(args.active.id).startsWith('goal:')) {
            return closestCenter(args);
        }

        const { collisionRect, droppableContainers, droppableRects } = args;
        if (lastTop != null && collisionRect.top !== lastTop) {
            movingDown = collisionRect.top > lastTop;
        }
        lastTop = collisionRect.top;
        const y = movingDown ? collisionRect.bottom : collisionRect.top;
        const first = movingDown ? null : getFirstHeader();
        const standIn = (id: string | number) =>
            id === first?.headerKey
                ? droppableContainers.find(
                      (container) => container.id === first.nextKey,
                  )
                : undefined;

        for (const container of droppableContainers) {
            const rect = droppableRects.get(container.id);
            if (rect != null && y >= rect.top && y < rect.top + rect.height) {
                const target = standIn(container.id) ?? container;
                return [
                    {
                        id: target.id,
                        data: { droppableContainer: target, value: 0 },
                    },
                ];
            }
        }
        return closestCenter(args).map((collision) => {
            const target = standIn(collision.id);
            return target != null
                ? {
                      ...collision,
                      id: target.id,
                      data: { droppableContainer: target, value: 0 },
                  }
                : collision;
        });
    };

    return {
        detect,
        reset: () => {
            lastTop = null;
            movingDown = true;
        },
    };
};

const getScrollContainer = (from: HTMLElement): HTMLElement | null => {
    let node = from.parentElement;
    while (node != null) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if (
            (overflowY === 'auto' || overflowY === 'scroll') &&
            node.scrollHeight > node.clientHeight
        ) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
};

const getScrollTop = (container: HTMLElement | null) =>
    container != null ? container.scrollTop : window.scrollY;

const getViewport = (container: HTMLElement | null) =>
    container != null
        ? {
              top: container.getBoundingClientRect().top,
              height: container.clientHeight,
          }
        : { top: 0, height: window.innerHeight };

const scrollViewportBy = (
    container: HTMLElement | null,
    delta: number,
    smooth: boolean,
) => {
    if (container == null) {
        window.scrollTo({
            top: window.scrollY + delta,
            behavior: smooth ? 'smooth' : 'auto',
        });
    } else if (smooth) {
        // react-native-web replaces `scrollTo` on a ScrollView's element with
        // its own that takes { x, y, animated }. Called the standard way it
        // reads y as undefined and scrolls to the top, so call the native one.
        // eslint-disable-next-line @typescript-eslint/unbound-method -- called with `container` as `this` below
        const nativeScrollTo = Element.prototype.scrollTo as (
            this: Element,
            options: ScrollToOptions,
        ) => void;
        nativeScrollTo.call(container, {
            top: container.scrollTop + delta,
            behavior: 'smooth',
        });
    } else {
        container.scrollTop += delta;
    }
};

export const MoneyMapEditableRulesList = ({
    accounts,
    canAddGoals,
    currentUserId,
    isReorderable = false,
}: MoneyMapRoutingRulesListProps) => {
    const [editableMoneyMap, setEditableMoneyMap] =
        useAtom(editableMoneyMapAtom);
    const setSelectedRule = useSetAtom(selectedMoneyMapRuleAtom);
    const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
    const [editingRuleSet, setEditingRuleSet] = useState<{
        id: string;
        name: string;
    } | null>(null);
    const [isSortingSets, setIsSortingSets] = useState(false);
    // The goal being dragged, while every set is condensed around it.
    const [draggedGoalRow, setDraggedGoalRow] = useState<FlatRow | null>(null);
    const isCondensingGoals = draggedGoalRow != null;
    // The set being dragged and the row it is over, for numbering the sets live.
    const [draggedSetKey, setDraggedSetKey] = useState<string | null>(null);
    const [overRowKey, setOverRowKey] = useState<string | null>(null);
    const isCondensed = isSortingSets || isCondensingGoals;
    // Space added above the list while sorting sets, for when the page can't
    // be scrolled far enough up to keep the grabbed header under the pointer.
    const [topSpacer, setTopSpacer] = useState(0);
    const [isAddRuleSetOpen, setIsAddRuleSetOpen] = useState(false);
    const [addingGoalRuleSetId, setAddingGoalRuleSetId] = useState<
        string | null
    >(null);

    const collision = useRef(
        createCollisionDetection(() => {
            const [first, next] = visibleRowsRef.current;
            return first?.kind === 'set-header' && next != null
                ? { headerKey: first.key, nextKey: next.key }
                : null;
        }),
    );
    const listNode = useRef<HTMLDivElement | null>(null);
    const rowNodes = useRef(new Map<string, HTMLElement>());
    const visibleRowsRef = useRef<FlatRow[]>([]);
    // Where the grabbed row was on screen when its drag began.
    const dragStart = useRef<{ key: string; top: number } | null>(null);
    // The rows to bring back into view once everything has expanded again after
    // a drop (a goal, or a set's header, goals and footer), and where the first
    // of them was on screen when it was dropped.
    const dropped = useRef<{ keys: string[]; top: number | null } | null>(null);

    const ruleSets = useMemo(() => {
        if (!editableMoneyMap) return [];
        return computeEditableRuleSets(editableMoneyMap, accounts);
    }, [editableMoneyMap, accounts]);

    const monthlyExpensesSet = ruleSets.find(
        (ruleSet) => ruleSet.id === MONTHLY_EXPENSES_SET_ID,
    );
    const goalSets = useMemo(
        () =>
            ruleSets.filter(
                (ruleSet) => ruleSet.id !== MONTHLY_EXPENSES_SET_ID,
            ),
        [ruleSets],
    );
    const rows = useMemo(() => flattenRuleSets(goalSets), [goalSets]);
    const visibleRows = useMemo(
        () =>
            isSortingSets && COLLAPSE_SETS_WHILE_DRAGGING
                ? getSetHeaderRows(rows)
                : rows,
        [isSortingSets, rows],
    );
    visibleRowsRef.current = visibleRows;
    const rowIds = useMemo(
        () => visibleRows.map((row) => row.key),
        [visibleRows],
    );

    const firstStopSetId = useMemo(
        () => getFirstIncomeSplitStopRuleSetId(ruleSets),
        [ruleSets],
    );

    const overBudgetCents = useMemo(() => {
        if (!editableMoneyMap) return 0;
        const billSpendRules = editableMoneyMap.moneyMapRules.filter(
            (r) =>
                r.type === MoneyMapRuleType.BILLS ||
                r.type === MoneyMapRuleType.SPEND,
        );
        const totalBillsSpend = billSpendRules.reduce((sum, rule) => {
            if (
                rule.routingRuleType === PaycheckRoutingRuleType.MINIMUM_BALANCE
            ) {
                return sum + (rule.targetBalanceCents ?? 0);
            }
            if (rule.routingRuleType === PaycheckRoutingRuleType.PERCENTAGE) {
                return sum + (rule.monthlyContributionMaxCents ?? 0);
            }
            return (
                sum +
                (rule.targetBalanceCents ??
                    rule.monthlyContributionMaxCents ??
                    0)
            );
        }, 0);
        return Math.max(
            0,
            totalBillsSpend - editableMoneyMap.monthlyIncomeCents,
        );
    }, [editableMoneyMap]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    );

    const handleEditRule = (ruleId: string) => {
        if (!editableMoneyMap) return;
        const rule = editableMoneyMap.moneyMapRules.find(
            (r) => r.id === ruleId,
        );
        if (rule) {
            setSelectedRule(rule);
            setIsEditSheetOpen(true);
        }
    };

    const liveOrdinals = useMemo(() => {
        if (draggedSetKey == null || overRowKey == null) return null;
        const from = visibleRows.findIndex((row) => row.key === draggedSetKey);
        const to = visibleRows.findIndex((row) => row.key === overRowKey);
        if (from < 0 || to < 0) return null;
        return new Map(
            getSetHeaderRows(arrayMove(visibleRows, from, to)).map(
                (row, index) => [row.key, index + 1],
            ),
        );
    }, [draggedSetKey, overRowKey, visibleRows]);

    const rowContext: RowContext = {
        canAddGoals,
        firstStopSetId,
        isCollapsed: isSortingSets && COLLAPSE_SETS_WHILE_DRAGGING,
        isCondensingGoals,
        draggedGoalKey: draggedGoalRow?.key ?? null,
        liveOrdinals,
        onEditRule: handleEditRule,
        onEditRuleSet: (ruleSet) => {
            setEditingRuleSet({ id: ruleSet.id, name: ruleSet.name });
        },
        onAddGoal: setAddingGoalRuleSetId,
    };

    // Screen position of a row, worked out from the list's position and the
    // heights of the rows above it. getBoundingClientRect on the row itself
    // would include dnd-kit's drag transforms and drop animations.
    const getRowViewportTop = (
        key: string,
        order: FlatRow[] = visibleRowsRef.current,
    ): number | null => {
        const list = listNode.current;
        if (list == null) return null;
        let top = list.getBoundingClientRect().top;
        for (const row of order) {
            if (row.key === key) return top;
            top += rowNodes.current.get(row.key)?.offsetHeight ?? 0;
        }
        return null;
    };

    // ---- Dragging a set or a goal -------------------------------------------
    //
    // Sets collapse to their headers while one is dragged, and every set
    // condenses to placeholders while a goal is. That moves the grabbed row up
    // the page, away from the pointer and away from the slot dnd-kit thinks it
    // occupies, so once the condensed layout is in place scroll the page by the
    // same amount to put the row back where it was.
    useLayoutEffect(() => {
        const start = dragStart.current;
        if (!isCondensed || start == null) return;
        dragStart.current = null;

        const top = getRowViewportTop(start.key);
        const list = listNode.current;
        if (top == null || list == null) return;

        const container = getScrollContainer(list);
        const delta = top - start.top;
        const scrollTop = getScrollTop(container);
        if (Math.abs(delta) <= 1) return;
        if (scrollTop + delta >= 0) {
            scrollViewportBy(container, delta, false);
        } else {
            // The page can't scroll above its top, so scroll as far as it goes
            // and make up the rest with space above the list.
            scrollViewportBy(container, -scrollTop, false);
            setTopSpacer(-(scrollTop + delta));
        }
    }, [isCondensed]);

    // After a drop everything expands again, which moves what was dropped.
    // First scroll so it's back where it was dropped, so it doesn't jump, then
    // scroll the rest of it (a whole set) into view.
    useLayoutEffect(() => {
        const drop = dropped.current;
        const list = listNode.current;
        if (isCondensed || drop == null || list == null) return;
        dropped.current = null;

        const firstKey = drop.keys[0];
        if (firstKey == null) return;
        const container = getScrollContainer(list);

        const expandedTop = getRowViewportTop(firstKey);
        if (expandedTop == null) return;
        if (drop.top != null && Math.abs(expandedTop - drop.top) > 1) {
            scrollViewportBy(container, expandedTop - drop.top, false);
        }

        const top = getRowViewportTop(firstKey);
        if (top == null) return;
        const height = drop.keys.reduce(
            (sum, key) => sum + (rowNodes.current.get(key)?.offsetHeight ?? 0),
            0,
        );
        const viewport = getViewport(container);
        const relativeTop = top - viewport.top;
        const relativeBottom = relativeTop + height;

        let delta = 0;
        if (relativeTop < SCROLL_INTO_VIEW_MARGIN) {
            delta = relativeTop - SCROLL_INTO_VIEW_MARGIN;
        } else if (relativeBottom > viewport.height - SCROLL_INTO_VIEW_MARGIN) {
            // Never scroll the top of something tall out of view.
            delta = Math.min(
                relativeBottom - (viewport.height - SCROLL_INTO_VIEW_MARGIN),
                relativeTop - SCROLL_INTO_VIEW_MARGIN,
            );
        }
        if (delta !== 0) scrollViewportBy(container, delta, true);
    }, [isCondensed, rows]);

    const handleDragStart = (event: DragStartEvent) => {
        collision.current.reset();
        const row = visibleRows.find((r) => r.key === event.active.id);
        if (row?.kind !== 'set-header' && row?.kind !== 'goal') return;

        // Not dnd-kit's active.rect.current.initial: it isn't filled in yet while
        // onDragStart runs.
        const initialTop = getRowViewportTop(row.key);
        if (initialTop != null) {
            dragStart.current = { key: row.key, top: initialTop };
        }
        if (row.kind === 'goal') {
            setDraggedGoalRow(row);
        } else {
            setIsSortingSets(true);
            setDraggedSetKey(row.key);
            setOverRowKey(row.key);
        }
    };

    // `order` is the condensed list as it stands at the drop, so the dropped
    // row's position is that of the slot it's dropping into.
    const finishDrag = (keys: string[], order: FlatRow[]) => {
        const firstKey = keys[0];
        dropped.current = {
            keys,
            top: firstKey != null ? getRowViewportTop(firstKey, order) : null,
        };
        setIsSortingSets(false);
        setDraggedGoalRow(null);
        setDraggedSetKey(null);
        setOverRowKey(null);
        setTopSpacer(0);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        const dragged = visibleRows.find((row) => row.key === active.id);
        if (dragged == null) return;

        const oldIndex = rowIds.indexOf(String(active.id));
        const newIndex = over != null ? rowIds.indexOf(String(over.id)) : -1;
        const moved =
            newIndex >= 0 && newIndex !== oldIndex
                ? arrayMove(visibleRows, oldIndex, newIndex)
                : null;

        if (dragged.kind === 'set-header') {
            // Goals stay with their sets, so only the order of the headers
            // matters, however the other rows ended up in the flat list.
            if (moved != null && editableMoneyMap) {
                setEditableMoneyMap(
                    reorderRuleSetsById(
                        editableMoneyMap,
                        getSetIdsInOrder(moved),
                    ),
                );
            }
            finishDrag(
                rows
                    .filter((row) => row.setId === dragged.setId)
                    .map((row) => row.key),
                moved ?? visibleRows,
            );
            return;
        }

        if (dragged.kind === 'goal') {
            if (moved != null && editableMoneyMap) {
                setEditableMoneyMap(
                    applyReorderedGoals(editableMoneyMap, moved),
                );
            }
            finishDrag([dragged.key], moved ?? visibleRows);
        }
    };

    const handleDragCancel = (event: { active: { id: string | number } }) => {
        const dragged = visibleRows.find((row) => row.key === event.active.id);
        if (dragged?.kind === 'set-header') {
            finishDrag(
                rows
                    .filter((row) => row.setId === dragged.setId)
                    .map((row) => row.key),
                visibleRows,
            );
        } else if (dragged?.kind === 'goal') {
            finishDrag([dragged.key], visibleRows);
        }
    };

    if (!editableMoneyMap) return null;

    const registerNode = (key: string, node: HTMLElement | null) => {
        if (node != null) {
            rowNodes.current.set(key, node);
        } else {
            rowNodes.current.delete(key);
        }
    };

    const monthlyExpenses = monthlyExpensesSet != null && (
        <>
            <MonthlyExpensesCard
                ruleSet={monthlyExpensesSet}
                showStopWarning={monthlyExpensesSet.id === firstStopSetId}
                renderRule={(rule, percentageWarning) => (
                    <MoneyMapRoutingRuleCard
                        {...rule}
                        percentageWarning={percentageWarning}
                        editable
                        onPressEdit={() => {
                            handleEditRule(rule.ruleId);
                        }}
                    />
                )}
            />
            <View alignSelf="center" padding={8}>
                <ArrowStraight />
            </View>
        </>
    );

    const addSetSection = canAddGoals && (
        <>
            <View alignSelf="center" padding={8}>
                <ArrowStraight />
            </View>
            <Button
                text="Add goal set"
                variant={ButtonVariant.SECONDARY}
                onPress={() => {
                    setIsAddRuleSetOpen(true);
                }}
            />
        </>
    );

    const rowList = isReorderable ? (
        <DndContext
            sensors={sensors}
            collisionDetection={collision.current.detect}
            // Rows are direct children of the list wrapper, so this keeps a
            // dragged row inside the goals area instead of over the page content
            // above and below it.
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            // Rows appear, disappear and move while sets collapse and expand,
            // so drop targets must be re-measured rather than measured once.
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
            onDragStart={handleDragStart}
            onDragOver={(event) => {
                setOverRowKey(
                    event.over != null ? String(event.over.id) : null,
                );
            }}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <SortableContext
                items={rowIds}
                strategy={verticalListSortingStrategy}
            >
                {topSpacer > 0 && <div style={{ height: topSpacer }} />}
                <div ref={listNode}>
                    {visibleRows.map((row) => (
                        <SortableRow
                            key={row.key}
                            row={row}
                            ctx={rowContext}
                            registerNode={registerNode}
                        />
                    ))}
                </div>
            </SortableContext>
            {/* The lifted goal card follows the pointer while its row becomes a
                slot. It's drawn in the page body: inside the page's scroll view,
                which has a CSS transform, fixed positioning is relative to that
                view instead of the window and the card would be offset. */}
            {createPortal(
                <DragOverlay dropAnimation={null}>
                    {draggedGoalRow != null && (
                        <RowBody
                            row={draggedGoalRow}
                            ctx={{
                                ...rowContext,
                                isCondensingGoals: false,
                            }}
                            // An inert handle, so the card keeps its grip.
                            drag={{
                                setActivatorNodeRef: () => undefined,
                                listeners: undefined,
                                attributes: {},
                            }}
                            isDragging
                        />
                    )}
                </DragOverlay>,
                document.body,
            )}
        </DndContext>
    ) : (
        <div ref={listNode}>
            {rows.map((row) => (
                <div key={row.key}>
                    <RowBody row={row} ctx={rowContext} isDragging={false} />
                </div>
            ))}
        </div>
    );

    return (
        <>
            {monthlyExpenses}
            {rowList}
            {addSetSection}
            {overBudgetCents > 0 && (
                <View marginTop={8}>
                    <Notice
                        variant="danger"
                        title="Monthly expenses exceed take-home pay"
                        description={`Bills and spend exceed income by $${Math.floor(overBudgetCents / 100).toLocaleString()}. Reduce amounts or increase take-home pay.`}
                    />
                </View>
            )}
            {
                // Collapsing the sets shortens the page. Keep it long enough that
                // the scroll position isn't clamped, which would move the grabbed
                // header away from the pointer.
                isCondensed && <div style={{ height: window.innerHeight }} />
            }
            <MoneyMapRuleEditSheet
                isOpen={isEditSheetOpen}
                onClose={() => {
                    setIsEditSheetOpen(false);
                }}
                accounts={accounts}
            />
            <MoneyMapRuleSetEditSheet
                isOpen={editingRuleSet != null}
                onClose={() => {
                    setEditingRuleSet(null);
                }}
                ruleSetId={editingRuleSet?.id ?? null}
                ruleSetName={editingRuleSet?.name ?? null}
            />
            <MoneyMapAddGoalSheet
                isOpen={addingGoalRuleSetId != null}
                onClose={() => {
                    setAddingGoalRuleSetId(null);
                }}
                ruleGroupId={addingGoalRuleSetId}
                accounts={accounts}
                currentUserId={currentUserId}
            />
            <MoneyMapAddRuleSetSheet
                isOpen={isAddRuleSetOpen}
                onClose={() => {
                    setIsAddRuleSetOpen(false);
                }}
            />
        </>
    );
};
